import { createCRUDHook } from './createCRUDHook';
import { AxiosResponse } from 'axios';
import { EstimateDto, FilterOptionModel, JsonPatch } from 'utils/models';
import { EstimationItemDto } from 'utils/models/estimationItems.model';
import {
  FILTERED_ENTITIES,
  CRM_CUSTOMER_ESTIMATE,
  CRM_CUSTOMER_ESTIMATE_ITEM,
  SEND_ESTIMATION_PDF,
  DELETE_ALL_ITEM_BY_CUSTOMER_ESTIMATE_ID,
  GET_FILTERED_ESTIMATES,
  CREATE_BULK_ENTITY,
  CREATE_ESTIMATION_FROM_ORDER,
  SHIPMENT,
  GET_UPS_SHIPMENT_CARGE_FOR_ESTIMATE,
  GET_LTL_SHIPMENT_CARGE_FOR_ESTIMATE,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';

interface EstimateCustomOperations {
  getEstimateDataByFilteredEntities: (
    filteredEntities: FilterOptionModel
  ) => Promise<EstimateDto[]>;
  getEstimateItemData: () => Promise<EstimationItemDto[]>;
  addEstimateItemData: (param: EstimationItemDto) => Promise<EstimationItemDto>;
  updateEstimateItemData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
  deletEstimateItemData: (id: number) => Promise<boolean>;
  getEstimateItemDataByFE: (
    filteredEntities: FilterOptionModel
  ) => Promise<EstimationItemDto[]>;
  DeleteAllItemByCustomerEstimateId: (id: number) => Promise<boolean>;
  getFilteredEstimates: (filteredEntities: any) => Promise<EstimationItemDto[]>;
  addEstimateItemDataBulkEntity: (param: any[]) => Promise<EstimationItemDto>;
  sendEstimationPDF: (
    email: string,
    name: string,
    estimationNumber: string,
    file: any
  ) => Promise<any>;
  GetLtlShipmentChargeForEstimates: (data: any) => Promise<any>;
  GetUpsShipmentChargeForEstimates: (data: any) => Promise<any>;
  createEstimateFromOrder: (orderId: number) => Promise<number>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  EstimateDto,
  EstimateDto[],
  EstimateCustomOperations
>({
  entityName: 'Estimate',
  endpoint: CRM_CUSTOMER_ESTIMATE,

  customOperations: (auth) => ({
    getEstimateDataByFilteredEntities: async (
      filteredEntities: FilterOptionModel
    ): Promise<EstimateDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          CRM_CUSTOMER_ESTIMATE +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getEstimateItemData: async (): Promise<EstimationItemDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(CRM_CUSTOMER_ESTIMATE_ITEM);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addEstimateItemData: async (
      param: EstimationItemDto
    ): Promise<EstimationItemDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          CRM_CUSTOMER_ESTIMATE_ITEM,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateEstimateItemData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${CRM_CUSTOMER_ESTIMATE_ITEM}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    deletEstimateItemData: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${CRM_CUSTOMER_ESTIMATE_ITEM}/${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },

    getEstimateItemDataByFE: async (
      filteredEntities: FilterOptionModel
    ): Promise<EstimationItemDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          CRM_CUSTOMER_ESTIMATE_ITEM +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    DeleteAllItemByCustomerEstimateId: async (id: number): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedDelete(
          `${CRM_CUSTOMER_ESTIMATE_ITEM}${DELETE_ALL_ITEM_BY_CUSTOMER_ESTIMATE_ID}?CustomerEstimateId=${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return false;
      }
    },

    getFilteredEstimates: async (
      filteredEntities: any
    ): Promise<EstimationItemDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          CRM_CUSTOMER_ESTIMATE +
            GET_FILTERED_ESTIMATES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    addEstimateItemDataBulkEntity: async (
      param: any[]
    ): Promise<EstimationItemDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          CRM_CUSTOMER_ESTIMATE_ITEM + CREATE_BULK_ENTITY,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    sendEstimationPDF: async (
      email: string,
      name: string,
      estimationNumber: string,
      file: any
    ): Promise<any> => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await auth.makeAuthenticatedPost(
          CRM_CUSTOMER_ESTIMATE +
            SEND_ESTIMATION_PDF +
            `?email=${email}&&name=${name}&&number=${estimationNumber}`,
          formData
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetLtlShipmentChargeForEstimates: async (data: any) => {
      try {
        return await auth.makeAuthenticatedPost(
          SHIPMENT + GET_LTL_SHIPMENT_CARGE_FOR_ESTIMATE,
          data
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetUpsShipmentChargeForEstimates: async (data: any) => {
      try {
        return await auth.makeAuthenticatedPost(
          SHIPMENT + GET_UPS_SHIPMENT_CARGE_FOR_ESTIMATE,
          data
        );
      } catch (error) {
        return Promise.reject(error);
      }
    },

    createEstimateFromOrder: async (orderId: number): Promise<number> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          `${CRM_CUSTOMER_ESTIMATE}${CREATE_ESTIMATION_FROM_ORDER}?orderId=${orderId}`,
          null
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const EstimateContext = Context;
export const useEstimateManager = useManager;
export const useEstimate = useConsumer;
